import { Button, Field, Input, ModalDrawer, RadioGroup, useModalState } from '@egw/components'
import { useTranslation } from '@egw/i18n'
import { type FC, useEffect, useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

// Compononent to Be used within a FormProvider from react-hook-form
export const EditExecShareholder: FC = () => {
  const { t } = useTranslation('features-enrollment')
  const { openModal, closeModal, isOpen } = useModalState()

  const [defaultExecShareholderField, setDefaultExecShareholderField] = useState<boolean | undefined>(undefined)
  const [defaultDirectorPolicyMakerOfCompany, setDefaultDirectorPolicyMakerOfCompany] = useState<string | undefined>(
    undefined,
  )

  const {
    control,
    watch,
    setValue,
    trigger,
    formState: { errors: formErrors },
  } = useFormContext()

  const execShareholderField = watch('isDirectorPolicyMakerOfCompany') as boolean | undefined
  const directorPolicyMakerOfCompany = watch('directorPolicyMakerOfCompany') as string | undefined

  useEffect(() => {
    if (execShareholderField !== undefined && defaultExecShareholderField === undefined) {
      setDefaultExecShareholderField(execShareholderField)
    }
    if (directorPolicyMakerOfCompany !== undefined && !defaultDirectorPolicyMakerOfCompany) {
      setDefaultDirectorPolicyMakerOfCompany(directorPolicyMakerOfCompany)
    }
  }, [
    execShareholderField,
    directorPolicyMakerOfCompany,
    defaultDirectorPolicyMakerOfCompany,
    defaultExecShareholderField,
  ])

  const closeWithoutSaving = async (): Promise<void> => {
    setValue('isDirectorPolicyMakerOfCompany', defaultExecShareholderField)
    setValue('directorPolicyMakerOfCompany', defaultDirectorPolicyMakerOfCompany)
    void trigger()
    closeModal()
  }

  const closeWithSaving = (): void => {
    if (!execShareholderField && directorPolicyMakerOfCompany) {
      setValue('directorPolicyMakerOfCompany', '')
    }

    // Set Modal Default Values
    setDefaultExecShareholderField(execShareholderField)
    setDefaultDirectorPolicyMakerOfCompany(directorPolicyMakerOfCompany)

    closeModal()
  }

  // If execShareholderField is undefined or if execShareholderField is true but directorPolicyMakerOfCompany is false, disable the CTA
  const disableCTA = Boolean(
    execShareholderField === undefined || (execShareholderField && !directorPolicyMakerOfCompany),
  )

  return (
    <>
      <Field
        className="border-b py-sm"
        valueClassName="text-text-meta typo-primary-base-regular"
        value={t('edit_fields.executive_or_shared_holder_title')}
        rightValue={execShareholderField ? t('common.yes') : t('common.no')}
        testId="editExecShareholder"
        onEdit={openModal}
      />
      <ModalDrawer
        isOpen={isOpen}
        onClose={closeWithoutSaving}
        title={t('edit_fields.edit_executive_or_shared_holder')}
      >
        <>
          <p className="typo-primary-base-regular text-text-body mb-sm">
            {t('edit_fields.major_share_holder_description')}
          </p>
          <Controller
            name="isDirectorPolicyMakerOfCompany"
            control={control}
            rules={{
              required: true,
              validate: (value) => (typeof value === 'number' && !isNaN(value)) || 'Field is required',
            }}
            render={({ field: { ref: _, ...restField } }) => (
              <RadioGroup
                {...restField}
                value={restField.value ? 1 : 0}
                options={[
                  { value: 0, label: t('common.no') },
                  { value: 1, label: t('common.yes') },
                ]}
                name="isDirectorPolicyMakerOfCompany"
                testId="is-director-policy-maker-of-company-radio"
              />
            )}
          />
          {execShareholderField ? (
            <>
              <p className="typo-primary-base-regular text-text-body mb-sm">
                {t('edit_fields.is_yes_major_share_holder')}
              </p>
              <Controller
                name="directorPolicyMakerOfCompany"
                control={control}
                rules={{
                  required: true,
                }}
                render={({ field }) => (
                  <Input
                    {...field}
                    placeholder={t('edit_fields.company_name_and_ticker')}
                    name="directorPolicyMakerOfCompany"
                    testId="enrollment-name-company-input"
                    className="mb-xl"
                    error={formErrors.directorPolicyMakerOfCompany?.message as string}
                  />
                )}
              />
            </>
          ) : null}

          <Button
            isDisabled={disableCTA}
            size="small"
            onClick={closeWithSaving}
            className="w-full"
            testId="primaryAction_button"
          >
            {t('common.confirm')}
          </Button>
        </>
      </ModalDrawer>
    </>
  )
}
